import { useCallback, useEffect, useRef, useState } from 'react'
import { getSession } from '@/lib/api'
import { websocketUrl } from '@/lib/config'
import type { Run } from '@/types/journey'
import type { BlueiotStatus, ConnectionStatus, LiveUser, ServerEvent } from '@/types/liveview'

interface LiveSocketState {
  users: Map<string, LiveUser>
  status: ConnectionStatus
  lastEventAt: number | null
  serverTime: string | null
  /**
   * The relay block off the last heartbeat, or `null` while none has arrived.
   * The server omits it for an organisation with no relay, so `null` also
   * means "there is nothing to report here".
   */
  blueiot: BlueiotStatus | null
  /**
   * The last `run` event, if any. Runs are their own slice, so this is only the
   * delivery channel — `useJourneyRuns` folds it into the table it keeps.
   */
  run: Run | null
  /**
   * Bumped on every `snapshot`. A lagging viewer is resynced with a snapshot
   * *only* and could have missed a terminal `run` event while it was behind, so
   * anything derived from run state refetches on this rather than trusting the
   * events it did receive.
   */
  snapshotNonce: number
  error: string | null
}

const RECONNECT_BASE_MS = 700
const RECONNECT_MAX_MS = 15_000

/**
 * How long a silent-but-open socket is tolerated before it is torn down.
 *
 * The server heartbeats every ~15s and pings every 20s, so nothing legitimate
 * stays quiet this long. A TCP connection that died without a FIN — a sleeping
 * laptop, a NAT rebind, a proxy that dropped the tunnel — otherwise leaves the
 * map frozen on stale positions while claiming to be Live, which is worse than
 * showing nothing.
 */
const SILENCE_TIMEOUT_MS = 45_000
const WATCHDOG_INTERVAL_MS = 5_000
const DEFAULT_TRAIL_LIMIT = 120

/**
 * One websocket to the LiveView API, with backoff-and-resubscribe.
 *
 * The server sends a full snapshot on connect (and again whenever a viewer has
 * lagged), so a reconnect never leaves the map showing positions the server has
 * since forgotten — the incoming snapshot replaces the table wholesale.
 */
export function useLiveSocket() {
  const [state, setState] = useState<LiveSocketState>({
    users: new Map(),
    status: 'connecting',
    lastEventAt: null,
    serverTime: null,
    blueiot: null,
    run: null,
    snapshotNonce: 0,
    error: null,
  })

  const socketRef = useRef<WebSocket | null>(null)
  const attemptRef = useRef(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closedByUsRef = useRef(false)
  /** Whether the current attempt ever reached `open`. */
  const openedRef = useRef(false)
  const lastMessageAtRef = useRef(Date.now())
  const trailLimitRef = useRef(DEFAULT_TRAIL_LIMIT)

  const applyEvent = useCallback((event: ServerEvent) => {
    lastMessageAtRef.current = Date.now()
    setState((prev) => {
      const users = new Map(prev.users)
      let serverTime = prev.serverTime
      let blueiot = prev.blueiot
      let run = prev.run
      let snapshotNonce = prev.snapshotNonce

      switch (event.type) {
        case 'snapshot':
          users.clear()
          for (const user of event.users) users.set(user.user_id, user)
          trailLimitRef.current = event.trail_limit ?? DEFAULT_TRAIL_LIMIT
          serverTime = event.server_time
          snapshotNonce += 1
          break
        case 'position': {
          // Position events ship without the trail; the client owns it and
          // appends the single new breadcrumb the server sent.
          const previous = users.get(event.user.user_id)
          let trail = previous?.trail ?? []
          if (event.point) {
            trail = [...trail, event.point]
            if (trail.length > trailLimitRef.current) {
              trail = trail.slice(trail.length - trailLimitRef.current)
            }
          }
          users.set(event.user.user_id, { ...event.user, trail })
          break
        }
        case 'stale': {
          const existing = users.get(event.user_id)
          if (existing) users.set(event.user_id, { ...existing, online: false })
          break
        }
        case 'removed':
          users.delete(event.user_id)
          break
        case 'run':
          run = event.run
          break
        case 'heartbeat':
          serverTime = event.server_time
          blueiot = event.blueiot ?? null
          break
      }

      return {
        ...prev,
        users,
        serverTime,
        blueiot,
        run,
        snapshotNonce,
        lastEventAt: Date.now(),
        error: null,
      }
    })
  }, [])

  const connect = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }

    let socket: WebSocket
    try {
      socket = new WebSocket(websocketUrl())
    } catch (error) {
      setState((prev) => ({ ...prev, status: 'error', error: String(error) }))
      return
    }
    socketRef.current = socket

    setState((prev) => ({
      ...prev,
      status: attemptRef.current === 0 ? 'connecting' : 'reconnecting',
    }))

    openedRef.current = false
    socket.onopen = () => {
      attemptRef.current = 0
      openedRef.current = true
      lastMessageAtRef.current = Date.now()
      setState((prev) => ({ ...prev, status: 'open', error: null }))
    }

    socket.onmessage = (message) => {
      try {
        applyEvent(JSON.parse(message.data as string) as ServerEvent)
      } catch {
        // A malformed frame is not worth tearing the connection down for.
      }
    }

    socket.onerror = () => {
      setState((prev) => ({ ...prev, error: 'websocket error' }))
    }

    socket.onclose = () => {
      socketRef.current = null
      if (closedByUsRef.current) return

      // A handshake the server refused looks, from here, exactly like a network
      // failure: the browser exposes no status for a rejected upgrade. Asking
      // the API who we are settles it — and a 401 there is what routes the app
      // back to the login screen, so a revoked session does not sit in an
      // endless reconnect loop.
      if (!openedRef.current) {
        getSession().catch(() => {})
      }

      const attempt = attemptRef.current++
      const delay = Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_MAX_MS)
      // Jitter keeps a wall of reopened tabs from stampeding the API.
      const jittered = delay * (0.75 + Math.random() * 0.5)
      setState((prev) => ({ ...prev, status: 'reconnecting' }))
      timerRef.current = setTimeout(connect, jittered)
    }
  }, [applyEvent])

  useEffect(() => {
    closedByUsRef.current = false
    connect()
    return () => {
      closedByUsRef.current = true
      if (timerRef.current) clearTimeout(timerRef.current)
      socketRef.current?.close()
      socketRef.current = null
    }
  }, [connect])

  // Watchdog: a socket the browser still calls OPEN but that has gone quiet is
  // closed, which routes it into the normal backoff-and-resubscribe path.
  useEffect(() => {
    const watchdog = setInterval(() => {
      const socket = socketRef.current
      if (!socket || socket.readyState !== WebSocket.OPEN) return
      if (Date.now() - lastMessageAtRef.current <= SILENCE_TIMEOUT_MS) return
      console.warn('[liveview] websocket went silent, reconnecting')
      socket.close()
    }, WATCHDOG_INTERVAL_MS)
    return () => clearInterval(watchdog)
  }, [])

  const reconnectNow = useCallback(() => {
    attemptRef.current = 0
    socketRef.current?.close()
    connect()
  }, [connect])

  return { ...state, reconnectNow }
}
